/**
 * «Переотправить фото» — общая модалка (задача B2, SPEC_B2_photo_resend.md).
 * Три шага: предпросмотр отбора → подтверждение → прогресс фоновой задачи с
 * честным итогом. Используется с трёх мест (вкладка «Карточки МП» карточки
 * макета, каталог товаров, «Показать наборы» не нужен здесь — ссылка ведёт в
 * журнал отправок), поэтому принимает произвольный вход `PhotoResendInput`
 * вместо завязки на конкретный экран.
 */

import { useEffect, useState } from 'react'
import { AlertTriangle, Send } from 'lucide-react'
import { Badge, Button, ConfirmDialog, Modal } from '../ui'
import { ImagePreviewThumb } from '../ui/ImagePreviewThumb'
import { marketplaceThumbnailUrl } from '../../shared/media/marketplaceThumbnails'
import { useAuthStore } from '../../stores/authStore'
import { useBackgroundJobsStore, type BackgroundJob } from '../../stores/backgroundJobsStore'
import { MARKETPLACE_LABELS, SEND_OUTCOME_LABELS } from '../../features/marketplace-card-changes/presentation'
import {
  photoResendApi,
  PHOTO_RESEND_READINESS_LABELS,
  photoResendReasonLabel,
  type PhotoResendInput,
  type PhotoResendItemResult,
  type PhotoResendJobSummary,
  type PhotoResendPreviewCardItem,
  type PhotoResendPreviewResult,
} from './photoResendApi'

const THUMBS_PER_ROW = 6

const SITE_STATUS_LABELS: Record<string, string> = {
  started: 'запущена перевыгрузка',
  dry_run: 'пробный прогон, перевыгрузка не запускалась',
  skipped: 'пропущено',
  failed: 'ошибка запуска перевыгрузки',
}

function errorMessage(error: any, fallback: string): string {
  return error?.response?.data?.error?.message || error?.message || fallback
}

function readyCount(items: PhotoResendPreviewCardItem[]): number {
  return items.filter((item) => item.readiness === 'ok').length
}

/** «ВБ: 3, ОЗОН: 2» — только площадки с ненулевым числом готовых карточек, фиксированный порядок. */
function readyBreakdownByMarketplace(items: PhotoResendPreviewCardItem[]): string {
  const order: Array<'wb' | 'ozon' | 'ym'> = ['wb', 'ozon', 'ym']
  const counts = new Map<string, number>()
  for (const item of items) {
    if (item.readiness !== 'ok') continue
    counts.set(item.marketplace, (counts.get(item.marketplace) || 0) + 1)
  }
  const parts = order
    .filter((code) => (counts.get(code) || 0) > 0)
    .map((code) => `${MARKETPLACE_LABELS[code]} ${counts.get(code)}`)
  return parts.join(', ')
}

export interface PhotoResendModalProps {
  isOpen: boolean
  onClose: () => void
  input: PhotoResendInput | null
  title?: string
}

export function PhotoResendModal({ isOpen, onClose, input, title }: PhotoResendModalProps) {
  const [preview, setPreview] = useState<PhotoResendPreviewResult | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)

  const job = useBackgroundJobsStore((state) => (jobId ? (state.jobs.get(jobId) ?? null) : null))

  useEffect(() => {
    if (!isOpen) {
      setPreview(null)
      setPreviewError(null)
      setConfirmOpen(false)
      setStartError(null)
      setJobId(null)
      return
    }
    if (!input) return
    const controller = new AbortController()
    setPreviewLoading(true)
    setPreviewError(null)
    photoResendApi
      .preview(input)
      .then((result) => {
        if (controller.signal.aborted) return
        setPreview(result)
      })
      .catch((error: any) => {
        if (controller.signal.aborted) return
        setPreviewError(errorMessage(error, 'Не удалось загрузить предпросмотр'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewLoading(false)
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, JSON.stringify(input)])

  const ready = preview ? readyCount(preview.items) : 0
  const siteCount = preview?.site.items.length ?? 0
  const sendEnabled = preview?.send_enabled ?? true
  const hasYm = preview?.items.some((item) => item.marketplace === 'ym') ?? false
  const ymWriteDisabled = hasYm && preview?.ym_write_enabled === false

  const runStart = async () => {
    if (!input || starting) return
    setConfirmOpen(false)
    setStarting(true)
    setStartError(null)
    try {
      const result = await photoResendApi.start(input, { dryRun: !sendEnabled })
      const jobsStore = useBackgroundJobsStore.getState()
      if (!jobsStore.jobs.has(result.jobId)) {
        jobsStore.addJob({
          jobId: result.jobId,
          userId: Number(useAuthStore.getState().user?.user_id ?? 0),
          type: 'marketplace_photo_resend',
          status: 'pending',
          name: 'Переотправка фото на площадки',
          currentStep: 'Ожидание запуска',
          progress: { current: 0, total: 0, percentage: 0 },
          steps: [],
          startedAt: new Date(),
          metadata: {},
        })
      }
      setJobId(result.jobId)
    } catch (error: any) {
      setStartError(errorMessage(error, 'Не удалось запустить переотправку фото'))
    } finally {
      setStarting(false)
    }
  }

  const summary = (job?.status === 'completed' ? (job.metadata as PhotoResendJobSummary | undefined) : undefined) ?? null
  const sendsJournalHref = jobId ? `/marketplace/card-changes?mode=sends&job_id=${encodeURIComponent(jobId)}` : null

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={starting ? () => undefined : onClose}
        title={title || 'Переотправить фото'}
        size="xl"
        closeOnOverlayClick={!starting}
        closeOnEsc={!starting}
        showCloseButton={!starting}
        footer={
          jobId ? (
            <Button variant="secondary" onClick={onClose}>
              Закрыть
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose} disabled={starting}>
                Отмена
              </Button>
              <Button
                variant="primary"
                icon={<Send className="h-4 w-4" />}
                loading={starting}
                disabled={previewLoading || !preview || ready === 0}
                onClick={() => setConfirmOpen(true)}
              >
                {sendEnabled ? `Отправить на ${ready} карточек` : `Пробный прогон (${ready})`}
              </Button>
            </>
          )
        }
      >
        <div className="space-y-4">
          {!jobId ? (
            <>
              {previewLoading && (
                <p className="py-8 text-center text-sm text-gray-500 dark:text-zinc-400">Считаем предпросмотр…</p>
              )}
              {previewError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  {previewError}
                </div>
              )}
              {startError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  {startError}
                </div>
              )}

              {preview && !previewLoading && (
                <>
                  {!sendEnabled && (
                    <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                      <p>
                        Отправка на площадки выключена (MP_CARD_CHANGES_SEND_ENABLED). Будет выполнен только
                        пробный прогон — на площадки ничего не уйдёт.
                      </p>
                    </div>
                  )}
                  {ymWriteDisabled && (
                    <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                      <p>Запись в Я.Маркет выключена — карточки Маркета уйдут только пробным прогоном.</p>
                    </div>
                  )}

                  {preview.items.length === 0 ? (
                    <p className="py-8 text-center text-sm text-gray-500 dark:text-zinc-400">
                      Подходящих опубликованных карточек не найдено.
                    </p>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-800">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs text-gray-500 dark:bg-zinc-900 dark:text-zinc-400">
                          <tr>
                            <th className="px-3 py-2">Площадка</th>
                            <th className="px-3 py-2">Кабинет</th>
                            <th className="px-3 py-2">Артикул</th>
                            <th className="px-3 py-2">Фото</th>
                            <th className="px-3 py-2 text-right">Всего</th>
                            <th className="px-3 py-2">Готовность</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                          {preview.items.map((item) => (
                            <tr key={`${item.marketplace}-${item.seller_id}-${item.article}`}>
                              <td className="px-3 py-2">
                                <Badge variant="indigo">{MARKETPLACE_LABELS[item.marketplace] || item.marketplace}</Badge>
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600 dark:text-zinc-300">{item.cabinet || '—'}</td>
                              <td className="px-3 py-2 font-mono text-xs font-medium text-gray-900 dark:text-zinc-100">
                                {item.article}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap items-center gap-1">
                                  {item.photos.slice(0, THUMBS_PER_ROW).map((photo, index) => (
                                    <ImagePreviewThumb
                                      key={`${photo.url}-${index}`}
                                      src={marketplaceThumbnailUrl(photo.url, 160)}
                                      alt={`${item.article} — фото ${index + 1}`}
                                      size="h-10 w-7"
                                      thumbnailWidth={96}
                                    />
                                  ))}
                                  {item.photos.length > THUMBS_PER_ROW && (
                                    <span className="text-2xs text-gray-500 dark:text-zinc-400">
                                      ещё {item.photos.length - THUMBS_PER_ROW}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-600 dark:text-zinc-300">
                                {item.photos_count}
                              </td>
                              <td className="px-3 py-2">
                                {item.readiness === 'ok' ? null : (
                                  <Badge variant="yellow">{PHOTO_RESEND_READINESS_LABELS[item.readiness]}</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {siteCount > 0 && (
                    <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300">
                      Сайт: {siteCount} {siteCount === 1 ? 'артикул' : 'артикулов'} — будут перевыгружены на сайт.
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <PhotoResendProgress
              job={job}
              summary={summary}
              sendsJournalHref={sendsJournalHref}
            />
          )}
        </div>
      </Modal>

      {preview && (
        <ConfirmDialog
          isOpen={confirmOpen}
          title={sendEnabled ? 'Отправить новые фото на площадки' : 'Пробный прогон переотправки фото'}
          message={
            sendEnabled ? (
              <>
                На площадки уйдут новые фото {ready} карточек ({readyBreakdownByMarketplace(preview.items) || 'нет готовых'}
                ){siteCount > 0 ? `, сайт — ${siteCount} ${siteCount === 1 ? 'артикул' : 'артикулов'}` : ''}. Это
                реальная отправка на боевые карточки, отменить её нельзя.
              </>
            ) : (
              <>
                Отправка на площадки выключена рубильником — уйдёт только пробный прогон: на площадки ничего не
                отправится. Будет проверено {ready} карточек ({readyBreakdownByMarketplace(preview.items) || 'нет готовых'}
                ){siteCount > 0 ? `, сайт — ${siteCount} ${siteCount === 1 ? 'артикул' : 'артикулов'}` : ''}.
              </>
            )
          }
          confirmLabel={sendEnabled ? 'Отправить' : 'Запустить пробный прогон'}
          cancelLabel="Отмена"
          loading={starting}
          onConfirm={() => void runStart()}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </>
  )
}

const ITEM_STATUS_TO_OUTCOME: Record<PhotoResendItemResult['status'], keyof typeof SEND_OUTCOME_LABELS> = {
  sent: 'sent',
  dry_run: 'dry_run',
  channel_disabled: 'channel_disabled',
  failed: 'failed',
  stale: 'stale',
  skipped: 'skipped',
}

function PhotoResendProgress({
  job,
  summary,
  sendsJournalHref,
}: {
  job: BackgroundJob | null
  summary: PhotoResendJobSummary | null
  sendsJournalHref: string | null
}) {
  if (!job) {
    return <p className="py-8 text-center text-sm text-gray-500 dark:text-zinc-400">Запускаем задачу…</p>
  }

  if (job.status === 'failed') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
        {job.error || 'Задача переотправки фото завершилась ошибкой.'}
      </div>
    )
  }

  if (job.status !== 'completed' || !summary) {
    return (
      <div className="space-y-2 py-4">
        <p className="text-sm text-gray-600 dark:text-zinc-300">{job.currentStep || 'Выполняется…'}</p>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-zinc-700">
          <div
            className="h-full bg-indigo-500 transition-all"
            style={{ width: `${Math.min(100, job.progress?.percentage ?? 0)}%` }}
          />
        </div>
      </div>
    )
  }

  const marketplaces = Object.keys(summary.by_marketplace)

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        {marketplaces.map((code) => {
          const breakdown = summary.by_marketplace[code]
          return (
            <div key={code} className="rounded-lg border border-gray-200 p-3 text-sm dark:border-zinc-800">
              <div className="mb-1.5 font-semibold text-gray-900 dark:text-zinc-100">
                {MARKETPLACE_LABELS[code as 'wb' | 'ozon' | 'ym'] || code}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-zinc-300">
                {(Object.keys(breakdown) as Array<keyof typeof breakdown>)
                  .filter((key) => breakdown[key] > 0)
                  .map((key) => (
                    <span key={String(key)}>
                      {SEND_OUTCOME_LABELS[ITEM_STATUS_TO_OUTCOME[key as PhotoResendItemResult['status']]].label}:{' '}
                      {breakdown[key]}
                    </span>
                  ))}
              </div>
            </div>
          )
        })}
      </div>

      {summary.site.articles_count > 0 && (
        <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300">
          Сайт: {SITE_STATUS_LABELS[summary.site.status] || summary.site.status} —{' '}
          {summary.site.articles_count} {summary.site.articles_count === 1 ? 'артикул' : 'артикулов'}
          {summary.site.reason ? ` (${photoResendReasonLabel(summary.site.reason)})` : ''}.
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500 dark:bg-zinc-900 dark:text-zinc-400">
            <tr>
              <th className="px-3 py-2">Артикул</th>
              <th className="px-3 py-2">Площадка</th>
              <th className="px-3 py-2">Статус</th>
              <th className="px-3 py-2">Причина</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
            {summary.items.map((item, index) => (
              <tr key={`${item.card_id}-${index}`}>
                <td className="px-3 py-2 font-mono text-xs font-medium text-gray-900 dark:text-zinc-100">
                  {item.article}
                </td>
                <td className="px-3 py-2">
                  <Badge variant="indigo">{MARKETPLACE_LABELS[item.marketplace] || item.marketplace}</Badge>
                </td>
                <td className="px-3 py-2">
                  <Badge variant={SEND_OUTCOME_LABELS[ITEM_STATUS_TO_OUTCOME[item.status]].variant}>
                    {SEND_OUTCOME_LABELS[ITEM_STATUS_TO_OUTCOME[item.status]].label}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 dark:text-zinc-400">{photoResendReasonLabel(item.reason)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sendsJournalHref && (
        <a
          href={sendsJournalHref}
          className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline dark:text-indigo-400"
        >
          Открыть журнал отправок
        </a>
      )}
    </div>
  )
}

export default PhotoResendModal
